/**
 * Delivery Utils — Haversine Distance + Smart Delivery Time Estimator
 * Format output: "30–35 mins • 1.5 km • Free Delivery"
 */

import { formatMoney } from './formatMoney';

/**
 * Haversine formula — calculates straight-line distance (km) between two lat/lng points
 */
export const haversineDistance = (lat1, lng1, lat2, lng2) => {
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;

  const R = 6371; // Earth radius in km
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // km
};

/**
 * Estimate delivery time based on:
 * - distance (km)
 * - restaurant prep time (min)
 * - time of day (rider availability simulation)
 */
export const estimateDeliveryTime = (distanceKm, prepTime = 15) => {
  if (distanceKm === null || distanceKm === undefined || isNaN(distanceKm)) {
    return { min: 25, max: 35 };
  }

  // If distance is far or outside typical delivery zone (> 30 km), default to standard 25-40 min
  if (distanceKm > 30) {
    return { min: 25, max: 40 };
  }

  // Rider speed on road = ~25 km/h on average (accounting for traffic)
  const avgSpeedKmPerMin = 25 / 60;

  // Travel time in minutes
  const travelTime = distanceKm / avgSpeedKmPerMin;

  // Rider availability factor based on time of day
  const hour = new Date().getHours();
  let riderFactor = 1.0;
  if (hour >= 12 && hour <= 14) riderFactor = 1.2; // Lunch rush
  else if (hour >= 19 && hour <= 21) riderFactor = 1.2; // Dinner rush
  else if (hour >= 0 && hour <= 6) riderFactor = 0.9; // Night

  // Total estimated time
  const rawTotal = (travelTime + prepTime) * riderFactor;

  // Round to nearest 5 min
  const roundedMin = Math.max(15, Math.ceil(rawTotal / 5) * 5);
  const rangeMax = roundedMin + 10;

  return { min: roundedMin, max: rangeMax };
};

/**
 * Format distance to readable string
 * e.g. 1.5 km, 800 m
 */
export const formatDistance = (distanceKm) => {
  if (distanceKm === null || distanceKm === undefined || isNaN(distanceKm)) return 'Nearby';

  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  if (distanceKm > 100) {
    return `${Math.round(distanceKm)} km`;
  }
  return `${distanceKm.toFixed(1)} km`;
};

/**
 * Main function — returns full delivery info string
 * Output: { timeLabel: "30–35 mins", distLabel: "1.5 km", deliveryFee: "Free Delivery", distanceKm: 1.5 }
 */
export const getDeliveryInfo = (restaurant, userLat, userLng, precalculatedDistance = null, currencySymbol = '') => {
  // Extract restaurant coordinates - handle multiple possible structures
  const restLat =
    restaurant?.location?.coordinates?.[1] ||
    restaurant?.coordinates?.lat ||
    restaurant?.lat ||
    restaurant?.latitude ||
    null;

  const restLng =
    restaurant?.location?.coordinates?.[0] ||
    restaurant?.coordinates?.lng ||
    restaurant?.lng ||
    restaurant?.longitude ||
    null;

  // Calculate distance or use pre-calculated one
  const distanceKm = precalculatedDistance !== null 
    ? precalculatedDistance 
    : (userLat && userLng && restLat && restLng ? haversineDistance(userLat, userLng, restLat, restLng) : null);
  
  // Format distance
  const distLabel = distanceKm !== null ? formatDistance(distanceKm) : 'Nearby';

  // Prep time from restaurant data (default 15 min)
  const prepTime = restaurant?.prepTime || restaurant?.averagePrepTime || 15;

  // Estimate time
  const timeRange =
    distanceKm !== null
      ? estimateDeliveryTime(distanceKm, prepTime)
      : restaurant?.deliveryTime
      ? { min: parseInt(restaurant.deliveryTime), max: parseInt(restaurant.deliveryTime) + 10 }
      : { min: 25, max: 35 };

  const timeLabel = timeRange
    ? timeRange.min === timeRange.max
      ? `${timeRange.min} mins`
      : `${timeRange.min}–${timeRange.max} mins`
    : '25–35 mins';

  // Delivery fee logic: ONLY free if <= 1.5 km
  const isWithinFreeRange = distanceKm !== null && distanceKm <= 1.5;
  
  const hasFreeDelivery =
    isWithinFreeRange ||
    restaurant?.isFreeDelivery ||
    restaurant?.freeDelivery;

  const deliveryFee = hasFreeDelivery 
    ? 'Free Delivery' 
    : `${formatMoney(restaurant?.deliveryCharge || 2.50, { symbol: currencySymbol })} delivery`;

  return {
    timeLabel,
    distLabel,
    deliveryFee,
    distanceKm,
  };
};
